import { useMemo, type ReactElement } from 'react';
import {
  useActivityHeatmap,
  dominantDiscipline,
  secondaryDiscipline,
  countActiveDisciplines,
  opacityForMinutes,
  type ActivityHeatmapDay,
} from '@/lib/dashboard';
import { DISCIPLINE_COLORS, type DisciplineKey } from '@/lib/disciplineColors';

/**
 * Heatmap estilo GitHub contributions para el dashboard — M2.17 rediseño.
 *
 * Cambios respecto M2.16:
 *  - El color de cada celda viene de la **disciplina dominante** del día
 *    (la que más minutos aporta; empate alfabético estable). Antes era la
 *    paleta verde plana por TSS.
 *  - La **opacidad** del color codifica la duración total (4-5 niveles):
 *    poca duración → muy translúcido, mucha → 100% opaco.
 *  - Si el día tiene >1 disciplina, dot pequeño (4 px) en la esquina
 *    inferior-derecha con el color de la 2ª disciplina (indicador "multi").
 *  - Click en celda (M2.18 — siempre muestra resumen, nunca navega):
 *      · `actualIds.length >= 1` → invoca `onDaySelect(day)` (Dashboard
 *        pinta el panel lateral con el resumen + botón "Ver detalle" por
 *        cada actividad). NO se navega directo aunque haya 1 sola
 *        actividad — el usuario quiere ver el resumen primero.
 *      · `0` actividades → no hace nada (no clicable).
 *  - Leyenda con 5 dots de disciplina + escala 4 cuadrados de opacidad.
 *
 * El shape de datos lo provee `useActivityHeatmap` (en `lib/dashboard.ts`,
 * M2.17) que pega a `/api/stats/activity-heatmap?days=N` y devuelve los
 * campos extra (`totalMinutes`, `byDiscipline`, `actualIds`).
 */

const DAYS_DEFAULT = 182; // ~26 semanas
const CELL_SIZE = 12;
const CELL_GAP = 2;
const ROW_LABEL_W = 24;
const HEADER_H = 16;

const EMPTY_FILL = '#e2e8f0'; // slate-200

export interface ActivityHeatmapProps {
  /** Número de días a mostrar (default: 182 ≈ 26 semanas). */
  days?: number;
  /**
   * Callback invocado cuando el usuario clickea una celda con ≥1 actividad.
   * El panel lateral renderiza el resumen y los botones "Ver detalle".
   */
  onDaySelect?: (day: ActivityHeatmapDay) => void;
}

export function ActivityHeatmap({
  days = DAYS_DEFAULT,
  onDaySelect,
}: ActivityHeatmapProps = {}): ReactElement {
  const query = useActivityHeatmap(days);

  return (
    <article
      aria-label="Mapa de actividad últimos meses"
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Mapa de actividad — últimos {Math.round(days / 7)} semanas
        </h2>
      </header>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="mt-4 text-sm text-slate-600">
          Cargando mapa…
        </p>
      )}
      {query.isError && (
        <p role="alert" className="mt-4 text-sm text-rose-700">
          No se pudo cargar el mapa de actividad.
        </p>
      )}
      {query.data && query.data.days.length > 0 && (
        <>
          <HeatmapGrid
            days={query.data.days}
            {...(onDaySelect ? { onDaySelect } : {})}
          />
          <HeatmapLegend />
        </>
      )}
      {query.data && query.data.days.length === 0 && (
        <p className="mt-4 text-sm text-slate-600">Aún no hay actividades para mostrar.</p>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Leyenda (5 disciplinas + escala de opacidad)
// ---------------------------------------------------------------------------

const LEGEND_DISCIPLINES: readonly DisciplineKey[] = [
  'swim',
  'bike',
  'run',
  'strength',
  'brick',
];
const OPACITY_SCALE = [0.25, 0.45, 0.7, 1] as const;

function HeatmapLegend(): ReactElement {
  return (
    <div
      data-testid="heatmap-legend"
      className="mt-4 flex flex-wrap items-center justify-between gap-3 text-[11px] text-slate-500"
    >
      <ul className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {LEGEND_DISCIPLINES.map((d) => (
          <li key={d} className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: DISCIPLINE_COLORS[d].hex }}
            />
            <span className="text-slate-700">{DISCIPLINE_COLORS[d].label}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-2">
        <span>menos</span>
        <span aria-hidden="true" className="text-slate-400">←</span>
        <ul className="flex gap-0.5">
          {OPACITY_SCALE.map((op) => (
            <li
              key={op}
              aria-hidden="true"
              className="h-3 w-3 rounded-sm border border-slate-200"
              style={{ background: DISCIPLINE_COLORS.bike.hex, opacity: op }}
              title={`Opacidad ${Math.round(op * 100)}%`}
            />
          ))}
        </ul>
        <span aria-hidden="true" className="text-slate-400">→</span>
        <span>duración</span>
        <span aria-hidden="true" className="text-slate-400">→</span>
        <span>más</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grid SVG
// ---------------------------------------------------------------------------

function HeatmapGrid({
  days,
  onDaySelect,
}: {
  days: ActivityHeatmapDay[];
  onDaySelect?: (day: ActivityHeatmapDay) => void;
}): ReactElement {
  const cells = useMemo(() => buildCells(days), [days]);
  const nWeeks = cells.weeks.length;
  const width = ROW_LABEL_W + nWeeks * (CELL_SIZE + CELL_GAP);
  const height = HEADER_H + 7 * (CELL_SIZE + CELL_GAP);

  function handleCellActivate(cell: CellData) {
    if (cell.actualIds.length === 0) return;
    // M2.18 — siempre mostrar resumen en panel lateral; el "Ver detalle"
    // por actividad está en el propio panel.
    onDaySelect?.(cell.day);
  }

  return (
    <div className="relative mt-4 overflow-x-auto" data-testid="activity-heatmap-grid">
      <svg
        role="img"
        aria-label={`Heatmap de actividad: ${days.length} días`}
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
      >
        {/* Headers de mes */}
        {cells.monthLabels.map((m) => (
          <text
            key={`${m.col}-${m.label}`}
            x={ROW_LABEL_W + m.col * (CELL_SIZE + CELL_GAP)}
            y={11}
            fontSize={9}
            fill="#64748b"
          >
            {m.label}
          </text>
        ))}

        {/* Etiquetas de día (Lun, Mié, Vie, Dom) */}
        {[
          { row: 0, label: 'L' },
          { row: 2, label: 'X' },
          { row: 4, label: 'V' },
          { row: 6, label: 'D' },
        ].map(({ row, label }) => (
          <text
            key={label}
            x={ROW_LABEL_W - 4}
            y={HEADER_H + row * (CELL_SIZE + CELL_GAP) + CELL_SIZE - 2}
            fontSize={9}
            fill="#94a3b8"
            textAnchor="end"
          >
            {label}
          </text>
        ))}

        {/* Cells */}
        {cells.weeks.map((week, weekIdx) =>
          week.map((cell, rowIdx) => {
            if (!cell) return null;
            const x = ROW_LABEL_W + weekIdx * (CELL_SIZE + CELL_GAP);
            const y = HEADER_H + rowIdx * (CELL_SIZE + CELL_GAP);
            const clickable = cell.actualIds.length > 0;
            const isEmpty = cell.totalMinutes <= 0;
            const fill = isEmpty
              ? EMPTY_FILL
              : DISCIPLINE_COLORS[cell.dominant ?? 'other'].hex;
            const opacity = isEmpty ? 1 : opacityForMinutes(cell.totalMinutes);
            return (
              <g key={`${weekIdx}-${rowIdx}`}>
                <rect
                  x={x}
                  y={y}
                  width={CELL_SIZE}
                  height={CELL_SIZE}
                  rx={2}
                  fill={fill}
                  opacity={opacity}
                  stroke="#e2e8f0"
                  strokeWidth={0.5}
                  data-testid={`heatmap-cell-${cell.dateISO}`}
                  data-discipline={cell.dominant ?? 'none'}
                  data-opacity={opacity}
                  style={clickable ? { cursor: 'pointer' } : undefined}
                  tabIndex={clickable ? 0 : -1}
                  role={clickable ? 'button' : undefined}
                  aria-label={
                    clickable
                      ? `${formatDate(cell.dateISO)}: ${cell.count} ${
                          cell.count === 1 ? 'actividad' : 'actividades'
                        }, ${Math.round(cell.totalMinutes)} min, ${Math.round(cell.tss)} TSS`
                      : undefined
                  }
                  onClick={clickable ? () => handleCellActivate(cell) : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleCellActivate(cell);
                          }
                        }
                      : undefined
                  }
                >
                  <title>
                    {formatDate(cell.dateISO)}: {cell.count}{' '}
                    {cell.count === 1 ? 'actividad' : 'actividades'} ·{' '}
                    {Math.round(cell.totalMinutes)} min · {Math.round(cell.tss)} TSS
                  </title>
                </rect>
                {cell.secondary !== null && (
                  <circle
                    cx={x + CELL_SIZE - 2}
                    cy={y + CELL_SIZE - 2}
                    r={2}
                    fill={DISCIPLINE_COLORS[cell.secondary].hex}
                    data-testid={`heatmap-multi-dot-${cell.dateISO}`}
                    pointerEvents="none"
                  />
                )}
              </g>
            );
          }),
        )}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Build logic
// ---------------------------------------------------------------------------

interface CellData {
  dateISO: string;
  tss: number;
  count: number;
  totalMinutes: number;
  dominant: DisciplineKey | null;
  secondary: DisciplineKey | null;
  actualIds: number[];
  /** Día completo (para emitir al callback `onDaySelect`). */
  day: ActivityHeatmapDay;
}

interface BuiltCells {
  weeks: Array<Array<CellData | null>>;
  monthLabels: Array<{ col: number; label: string }>;
}

function buildCells(days: ActivityHeatmapDay[]): BuiltCells {
  if (days.length === 0) {
    return { weeks: [], monthLabels: [] };
  }

  // Día de la semana (Lun=0..Dom=6) del primer día.
  const first = days[0]!;
  const firstWeekday = isoWeekday(first.dateISO);

  // Crear matriz weeks[col][row=0..6].
  const weeks: Array<Array<CellData | null>> = [];
  let currentWeek: Array<CellData | null> = new Array(7).fill(null);
  for (let i = 0; i < firstWeekday; i++) currentWeek[i] = null;

  let col = 0;
  let weekday = firstWeekday;
  const monthLabels: Array<{ col: number; label: string }> = [];
  let lastMonth = -1;

  for (const d of days) {
    const activeCount = countActiveDisciplines(d.byDiscipline);
    const cell: CellData = {
      dateISO: d.dateISO,
      tss: d.tss,
      count: d.count,
      totalMinutes: d.totalMinutes,
      dominant: dominantDiscipline(d.byDiscipline),
      secondary: activeCount > 1 ? secondaryDiscipline(d.byDiscipline) : null,
      actualIds: d.actualIds,
      day: d,
    };
    currentWeek[weekday] = cell;

    const month = monthOf(d.dateISO);
    if (month !== lastMonth) {
      monthLabels.push({ col, label: monthLabel(month) });
      lastMonth = month;
    }

    weekday += 1;
    if (weekday > 6) {
      weeks.push(currentWeek);
      currentWeek = new Array(7).fill(null);
      weekday = 0;
      col += 1;
    }
  }
  if (currentWeek.some((x) => x !== null)) {
    weeks.push(currentWeek);
  }

  return { weeks, monthLabels };
}

function isoWeekday(dateISO: string): number {
  const d = new Date(`${dateISO}T00:00:00.000Z`);
  const js = d.getUTCDay();
  return js === 0 ? 6 : js - 1;
}

function monthOf(dateISO: string): number {
  return Number(dateISO.slice(5, 7));
}

function monthLabel(m: number): string {
  const labels = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return labels[m] ?? '';
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      timeZone: 'UTC',
    }).format(new Date(`${iso}T00:00:00.000Z`));
  } catch {
    return iso;
  }
}
